import { Loader } from '@googlemaps/js-api-loader';

class GoogleMapsService {
  constructor() {
    this.apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    this.loader = null;
    this.google = null;
    this.map = null;
    this.placesService = null;
  }

  withTimeout(promise, message, milliseconds = 10000) {
    let timer;
    const timeout = new Promise((_, reject) => {
      timer = setTimeout(() => reject(new Error(message)), milliseconds);
    });
    return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
  }

  async ensureGoogle() {
    if (this.google) return this.google;
    if (!this.apiKey) throw new Error('尚未設定 Google Maps API 金鑰');

    this.loader ||= new Loader({
      apiKey: this.apiKey,
      version: 'weekly',
      libraries: ['places'],
      language: 'zh-TW',
      region: 'TW'
    });
    this.google = await this.withTimeout(this.loader.load(), 'Google Maps 載入逾時，請檢查 API 金鑰或稍後再試');
    return this.google;
  }

  async ensurePlaces() {
    if (this.placesService) return this.placesService;
    const google = await this.ensureGoogle();
    this.placesService = new google.maps.places.PlacesService(document.createElement('div'));
    return this.placesService;
  }

  async initializeMap(container, options = {}) {
    const google = await this.ensureGoogle();
    this.map = new google.maps.Map(container, {
      center: { lat: 25.033, lng: 121.5654 },
      zoom: 14,
      disableDefaultUI: true,
      zoomControl: true,
      ...options
    });
    this.placesService = new google.maps.places.PlacesService(this.map);
    return { map: this.map, placesService: this.placesService };
  }

  getCurrentLocation() {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('此裝置不支援定位，請手動輸入地點'));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        ({ coords }) => resolve({ lat: coords.latitude, lng: coords.longitude }),
        () => reject(new Error('無法取得位置，請允許定位或手動輸入地點')),
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
      );
    });
  }

  async geocodeAddress(address) {
    const google = await this.ensureGoogle();
    const geocoder = new google.maps.Geocoder();
    const { results } = await this.withTimeout(
      geocoder.geocode({ address, region: 'TW' }),
      '地點查詢逾時，請檢查 API 金鑰或稍後再試'
    );
    const result = results?.[0];
    if (!result) throw new Error('找不到這個地點，請換個關鍵字');
    return {
      location: {
        lat: result.geometry.location.lat(),
        lng: result.geometry.location.lng()
      },
      label: result.formatted_address,
      placeId: result.place_id
    };
  }

  async searchPlaces(request, method, allPages = false) {
    const service = await this.ensurePlaces();
    const google = await this.ensureGoogle();
    return new Promise((resolve, reject) => {
      const places = new Map();
      let pageTimer;
      let pageNumber = 1;
      let pendingPage;
      let pageRetries = 0;
      let finished = false;
      const finish = error => {
        if (finished) return;
        finished = true;
        clearTimeout(timeout); clearTimeout(pageTimer);
        if (error && (!allPages || !places.size)) reject(error);
        else resolve(allPages ? { places: [...places.values()], searchIncomplete: Boolean(error) } : [...places.values()]);
      };
      const timeout = setTimeout(() => finish(new Error('餐廳搜尋逾時，請稍後再試')), allPages ? 30000 : 10000);
      try {
        service[method](request, (results, status, pagination) => {
          if (finished) return;
          if (status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS) return finish();
          if (status === 'INVALID_REQUEST' && pendingPage && pageRetries < 2) {
            pageRetries++;
            pageTimer = setTimeout(() => { try { pendingPage.nextPage(); } catch (error) { finish(error); } }, 2000);
            return;
          }
          if (status !== google.maps.places.PlacesServiceStatus.OK) return finish(new Error(`餐廳搜尋失敗：${status}（第 ${pageNumber} 頁）`));
          pageRetries = 0;
          pendingPage = null;
          for (const place of results || []) if (place.place_id) places.set(place.place_id, place);
          if (allPages && pagination?.hasNextPage && places.size < 60) {
            pendingPage = pagination;
            pageTimer = setTimeout(() => { try { pageNumber++; pagination.nextPage(); } catch (error) { finish(error); } }, 2000);
          } else finish();
        });
      } catch (error) { finish(error); }
    });
  }

  searchNearbyPlaces(location, type = 'restaurant', radius = 1200, allPages = false) {
    return this.searchPlaces({ location, radius, type }, 'nearbySearch', allPages);
  }

  searchPlacesByText(query, location = null, radius = 1200, allPages = false) {
    const request = { query };
    if (location) Object.assign(request, { location, radius });
    return this.searchPlaces(request, 'textSearch', allPages);
  }

  async getPlaceAutocomplete(input, location = null, radius = 5000) {
    const google = await this.ensureGoogle();
    const service = new google.maps.places.AutocompleteService();
    return this.withTimeout(new Promise((resolve, reject) => {
      const request = { input, types: ['establishment'] };
      if (location) Object.assign(request, { location, radius });
      service.getPlacePredictions(request, (predictions, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK) resolve(predictions || []);
        else if (status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS) resolve([]);
        else reject(new Error(`自動完成失敗：${status}`));
      });
    }), '自動完成逾時，請稍後再試');
  }

  async getPlaceDetails(placeId) {
    const service = await this.ensurePlaces();
    const google = await this.ensureGoogle();
    return this.withTimeout(new Promise((resolve, reject) => {
      service.getDetails({
        placeId,
        fields: [
          'name', 'rating', 'user_ratings_total', 'formatted_phone_number',
          'formatted_address', 'opening_hours', 'photos', 'reviews', 'price_level',
          'website', 'url', 'types', 'editorial_summary', 'business_status', 'geometry',
          'utc_offset_minutes'
        ]
      }, (place, status) => {
        if (status === google.maps.places.PlacesServiceStatus.OK) resolve(place);
        else reject(new Error(`餐廳詳情載入失敗：${status}`));
      });
    }), '餐廳詳情載入逾時，請稍後再試');
  }

  async refreshSavedPlaces(places, isCurrent = () => true) {
    const refreshed = [];
    let failed = 0;
    for (let start = 0; start < places.length; start += 4) {
      if (!isCurrent()) throw new Error('搜尋條件已變更，請重新分析。');
      const batch = await Promise.allSettled(places.slice(start, start + 4).map(async place => {
        const details = await this.getPlaceDetails(place.place_id);
        return { ...place, ...details, opening_hours: details.opening_hours ? { open_now: details.opening_hours.isOpen?.() ?? details.opening_hours.open_now } : null };
      }));
      if (!isCurrent()) throw new Error('搜尋條件已變更，請重新分析。');
      for (const result of batch) {
        if (result.status === 'fulfilled') refreshed.push(result.value);
        else failed++;
      }
    }
    return { places: refreshed, failed };
  }

  getPhotoUrl(place, width = 900) {
    if (place?.photoUrl) return place.photoUrl;
    const photo = place?.photos?.[0] || place?.details?.photos?.[0];
    return typeof photo?.getUrl === 'function' ? photo.getUrl({ maxWidth: width }) : '';
  }

  calculateDistance(point1, point2) {
    if (!point1 || !point2) return null;
    const lat = value => typeof value === 'function' ? value() : value;
    const p1 = { lat: lat(point1.lat), lng: lat(point1.lng) };
    const p2 = { lat: lat(point2.lat), lng: lat(point2.lng) };
    if (![p1.lat, p1.lng, p2.lat, p2.lng].every(Number.isFinite) || Math.abs(p1.lat) > 90 || Math.abs(p2.lat) > 90 || Math.abs(p1.lng) > 180 || Math.abs(p2.lng) > 180) return null;
    const earth = 6371e3;
    const toRad = value => value * Math.PI / 180;
    const dLat = toRad(p2.lat - p1.lat);
    const dLng = toRad(p2.lng - p1.lng);
    const a = Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(p1.lat)) * Math.cos(toRad(p2.lat)) * Math.sin(dLng / 2) ** 2;
    return earth * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

}

export default new GoogleMapsService();
